import type { Typescales } from '@/common/view/typescales';
import colors from '@/common/view/colors';

const SKIN = {
  PRIMARY: 'primary',
  PRIMARY_OUTLINED: 'primary-outlined',
  BLACK: 'black',
  BLACK_OUTLINED: 'black-outlined',
  GRAY: 'gray',
  GRAY_OUTLINED: 'gray-outlined',
  DANGEROUS: 'dangerous',
  DANGEROUS_OUTLINED: 'dangerous-outlined',
  BRAND: 'brand',
} as const;
export type Skin = (typeof SKIN)[keyof typeof SKIN];

const SIZE = {
  MEDIUM: 'medium',
  SMALL: 'small',
  EXTRA_SMALL: 'xsmall',
} as const;
export type Size = (typeof SIZE)[keyof typeof SIZE];

export const getButtonTypo = (size: Size): Typescales => {
  switch (size) {
    case 'xsmall':
      return 'caption1';
    case 'small':
      return 'body5';
    case 'medium':
    default:
      return 'body2';
  }
};

export const getHeight = (size?: Size): string => {
  switch (size) {
    case 'xsmall':
      return '30px';
    case 'small':
      return '40px';
    case 'medium':
    default:
      return '44px';
  }
};
export const getPadding = (size?: Size): string => {
  switch (size) {
    case 'xsmall':
      return '6px 12px';
    case 'small':
      return '10px 16px';
    case 'medium':
    default:
      return '10px 20px';
  }
};

type ColorMap = { [key in Skin]: string };

export const getBgColor = (skin: Skin) => {
  const cmap: ColorMap = {
    primary: colors.blue50,
    'primary-outlined': colors.grayWhite,
    black: colors.gray100,
    'black-outlined': colors.grayWhite,
    gray: colors.gray20,
    'gray-outlined': colors.grayWhite,
    dangerous: colors.red50,
    'dangerous-outlined': colors.grayWhite,
    brand: colors.brand,
  };

  return cmap[skin];
};

export const getBorderColor = (skin: Skin) => {
  const cmap = {
    primary: colors.blue50,
    'primary-outlined': colors.blue50,
    black: colors.gray100,
    'black-outlined': colors.gray100,
    gray: colors.gray20,
    'gray-outlined': colors.gray30,
    dangerous: colors.red50,
    'dangerous-outlined': colors.red50,
    brand: colors.brand,
  };

  return cmap[skin];
};
export const getTextColor = (skin: Skin) => {
  const cmap: ColorMap = {
    primary: colors.grayWhite,
    'primary-outlined': colors.blue50,
    black: colors.grayWhite,
    'black-outlined': colors.gray100,
    gray: colors.gray100,
    'gray-outlined': colors.gray100,
    dangerous: colors.grayWhite,
    'dangerous-outlined': colors.red50,
    brand: colors.grayWhite,
  };
  return cmap[skin];
};

export const getHoverBorderColor = (skin: Skin) => {
  switch (skin) {
    case 'gray-outlined':
      return colors.grayBlack;
    case 'primary':
    case 'primary-outlined':
    case 'black':
    case 'black-outlined':
    case 'gray':
    case 'dangerous':
    case 'dangerous-outlined':
    case 'brand':
    default:
      return getBorderColor(skin);
  }
};

export const getFocusTextColor = (skin: Skin) => {
  switch (skin) {
    case 'gray-outlined':
      return colors.blue50;
    case 'primary':
    case 'primary-outlined':
    case 'black':
    case 'black-outlined':
    case 'gray':
    case 'dangerous':
    case 'dangerous-outlined':
    case 'brand':
    default:
      return getTextColor(skin);
  }
};

export const getDisabledBgColor = (skin: Skin) => {
  const cmap: ColorMap = {
    primary: colors.blue30,
    'primary-outlined': colors.grayWhite,
    black: colors.gray30,
    'black-outlined': colors.grayWhite,
    gray: colors.gray20,
    'gray-outlined': colors.grayWhite,
    dangerous: colors.red10,
    'dangerous-outlined': colors.grayWhite,
    brand: "#9370DB",
  };
  return cmap[skin];
};

export const getDisabledBorderColor = (skin: Skin) => {
  const cm: ColorMap = {
    primary: colors.blue30,
    'primary-outlined': colors.blue30,
    black: colors.gray30,
    'black-outlined': colors.gray30,
    gray: colors.gray20,
    'gray-outlined': colors.gray30,
    dangerous: colors.red10,
    'dangerous-outlined': colors.red10,
    brand: "#9370DB",
  };
  return cm[skin];
};

export const getDisabledTextColor = (skin: Skin) => {
  const cm: ColorMap = {
    primary: colors.grayWhite,
    'primary-outlined': colors.blue30,
    black: colors.grayWhite,
    'black-outlined': colors.gray30,
    gray: colors.gray30,
    'gray-outlined': colors.gray30,
    dangerous: colors.grayWhite,
    'dangerous-outlined': colors.red10,
    brand: colors.grayWhite,
  };
  return cm[skin];
};
